import type { DrawerTransport } from "./transport";
import { selectTransport } from "./select-transport";

/**
 * Capability detection (T131, FR-104).
 *
 * RUN ON EVERY TILL LOAD, NEVER CACHED ACROSS SESSIONS. Every one of these
 * facts can change while nobody is looking: a browser update, a revoked USB
 * grant, a device reinstall, an unplugged cable over the weekend. A cached
 * "yes, the drawer works" is the worst possible answer, because the till would
 * present drawer control as available and only discover otherwise at the moment
 * a customer is waiting for change.
 *
 * The four flags are deliberately separate rather than one boolean, because
 * each implies a DIFFERENT recovery and telling staff the wrong one wastes
 * their time:
 *
 *   transportSupported: false
 *                        → this browser exposes neither WebUSB nor Web
 *                          Bluetooth, so it can never do it. Nothing to fix
 *                          here; say so plainly and use the manual flow
 *                          (FR-104).
 *   deviceGranted: false → nobody has picked a device yet, or the grant was
 *                          lost. Fixable in settings, needs a user gesture.
 *   connected: false     → granted, but not answering. A cable or power problem.
 *   drawerStatus: false  → this printer cannot report drawer state. The
 *                          left-open alert is then ABSENT, not guessed (FR-106).
 */

export interface DrawerCapability {
  transportSupported: boolean;
  deviceGranted: boolean;
  connected: boolean;
  drawerStatus: boolean;
}

export const UNSUPPORTED: DrawerCapability = {
  transportSupported: false,
  deviceGranted: false,
  connected: false,
  drawerStatus: false,
};

/**
 * Probe the current state.
 *
 * Never throws. A capability probe that fails must degrade to "unavailable"
 * rather than break the till load — drawer control is an enhancement, and the
 * sale path does not depend on it.
 */
export async function detectCapability(
  transport: DrawerTransport = selectTransport(),
): Promise<DrawerCapability> {
  if (!transport.isSupported()) return UNSUPPORTED;

  try {
    const state = await transport.connect();

    if (state === "permission_lost") {
      return { transportSupported: true, deviceGranted: false, connected: false, drawerStatus: false };
    }

    const connected = state === "connected";
    return {
      transportSupported: true,
      deviceGranted: true,
      connected,
      // Detected from the device's endpoints, never assumed from a model name.
      drawerStatus: connected && transport.hasDrawerStatus(),
    };
  } catch {
    return { transportSupported: true, deviceGranted: false, connected: false, drawerStatus: false };
  }
}

/** Phrased for a person behind a counter, not an engineer. */
export function describeCapability(capability: DrawerCapability): string {
  if (!capability.transportSupported) {
    return "This device cannot open a cash drawer. Drawer opens will be recorded by hand.";
  }
  if (!capability.deviceGranted) {
    return "No drawer is set up on this terminal yet. Connect one in settings.";
  }
  if (!capability.connected) {
    return "The drawer is set up but not responding. Check that it is plugged in and switched on.";
  }
  return capability.drawerStatus
    ? "Drawer connected. This printer can also report whether the drawer is open."
    : "Drawer connected. This printer cannot report whether the drawer is open.";
}
